import { usePricing } from '@/hooks/usePricing';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { GAP_TITLES, GAP_NARRATIVES, GAP_HOMEWORK, buildMirrorCopy, TOKENS } from './glowUpGapNarratives';
import GlowUpGapVideoSection from './GlowUpGapVideoSection';

const API = `${window.location.origin}/api`;

/**
 * Glow Up Gap shareable report — read-only view of a completed assessment.
 * Route: /glow-up-gap/report/:reportId
 * Standalone (no header/footer). Mirrors the in-flow Results screen.
 */
export default function GlowUpGapReport() {
  const { reportId } = useParams();
  const pricing = usePricing();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Your Glow Up Gap Report | Dea';
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchReport = async () => {
      try {
        const resp = await fetch(`${API}/glow-up-gap/report/${reportId}`);
        if (!resp.ok) {
          if (resp.status === 404) {
            throw new Error("We couldn't find that report. The link may be incorrect or expired.");
          }
          throw new Error(`HTTP ${resp.status}`);
        }
        const data = await resp.json();
        if (alive) {
          setReport(data);
          setLoading(false);
        }
      } catch (err) {
        if (alive) {
          setError(err.message || 'Something went wrong loading your report.');
          setLoading(false);
        }
      }
    };
    fetchReport();
    return () => { alive = false; };
  }, [reportId]);

  const handleStartTrial = () => {
    if (!report) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      // Go straight to the branded inline checkout (same URL the in-app flow uses).
      // Email is included in the public report response specifically for this prefill.
      const params = new URLSearchParams({
        plan: 'glow-up-trial',
        email: report.email || '',
        first_name: report.first_name || '',
        report_id: reportId || '',
      });
      window.location.href = `/checkout?${params.toString()}`;
    } catch (err) {
      setCheckoutError("Couldn't start checkout. Please try again in a moment.");
      setCheckoutLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: TOKENS.bg,
        color: TOKENS.dark,
        fontFamily: TOKENS.sans,
        padding: '40px 20px 64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {loading && (
        <div style={{ paddingTop: '60px', textAlign: 'center' }}>
          <Loader2 size={28} style={{ color: TOKENS.gold, animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '14px', color: TOKENS.muted, fontSize: '14px' }}>Loading your report…</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {error && !loading && (
        <div style={{ paddingTop: '60px', textAlign: 'center', maxWidth: 420 }} data-testid="glowup-report-error">
          <p style={{ fontFamily: TOKENS.serif, fontSize: '22px', color: TOKENS.dark, marginBottom: '12px' }}>
            We couldn&apos;t find that report.
          </p>
          <p style={{ color: TOKENS.muted, fontSize: '14px', lineHeight: 1.6 }}>{error}</p>
          <a
            href="/glow-up-gap"
            style={{
              display: 'inline-block',
              marginTop: '24px',
              background: TOKENS.gold,
              color: '#fff',
              padding: '14px 24px',
              borderRadius: '30px',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            Take the assessment →
          </a>
        </div>
      )}

      {report && !error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ width: '100%', maxWidth: 620 }}
          data-testid="glowup-report-content"
        >
          <div
            style={{
              color: TOKENS.gold,
              fontSize: '11px',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontWeight: 600,
              textAlign: 'center',
              marginBottom: '32px',
            }}
          >
            {report.first_name ? `${report.first_name}, Your` : 'Your'} Glow Up Gap
          </div>

          <div style={{ textAlign: 'center' }}>
            <h1
              style={{
                fontFamily: TOKENS.serif,
                fontSize: 'clamp(36px, 8vw, 56px)',
                fontWeight: 400,
                color: TOKENS.dark,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: '-0.01em',
              }}
            >
              {GAP_TITLES[report.primary_gap] || ''}
            </h1>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '80px' }}
              transition={{ duration: 0.6, delay: 0.25, ease: 'easeOut' }}
              style={{ height: '2px', background: TOKENS.gold, margin: '14px auto 0' }}
            />
          </div>

          {report.secondary_gap && (
            <p
              style={{
                marginTop: '24px',
                fontSize: '14px',
                fontStyle: 'italic',
                color: TOKENS.muted,
                textAlign: 'center',
              }}
              data-testid="glowup-report-secondary"
            >
              You may also be experiencing a secondary gap in {GAP_TITLES[report.secondary_gap]}.
            </p>
          )}

          <div style={{ marginTop: '36px' }}>
            {(GAP_NARRATIVES[report.primary_gap] || []).map((p, i) => (
              <p
                key={i}
                style={{
                  fontFamily: TOKENS.sans,
                  fontSize: '16px',
                  lineHeight: 1.65,
                  color: TOKENS.dark,
                  margin: '0 0 18px 0',
                }}
              >
                {p}
              </p>
            ))}
          </div>

          {/* PERSONALIZED VIDEO MESSAGE — one per primary_gap.
              Sits between the narrative and the "Start Today" homework card per spec. */}
          <GlowUpGapVideoSection primaryGap={report.primary_gap} tokens={TOKENS} reportId={reportId} />

          {/* HOMEWORK CARD (v3) */}
          <div
            style={{
              background: TOKENS.cardBg,
              border: `1px solid ${TOKENS.cardBorder}`,
              borderLeft: `3px solid ${TOKENS.gold}`,
              borderRadius: '12px',
              padding: '24px',
              margin: '28px 0 24px',
            }}
            data-testid="glowup-report-homework"
          >
            <p style={{ color: TOKENS.gold, fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, margin: '0 0 12px 0' }}>
              Start Today — Your One Move
            </p>
            <p style={{ fontSize: '13px', lineHeight: 1.7, color: TOKENS.muted, margin: '0 0 14px 0' }}>
              Before you do anything else, here&apos;s one small thing you can do right now to start closing your {GAP_TITLES[report.primary_gap] || ''} gap:
            </p>
            <p style={{ fontSize: '15px', lineHeight: 1.7, color: TOKENS.dark, margin: '0 0 14px 0' }}>
              {GAP_HOMEWORK[report.primary_gap] || ''}
            </p>
            <p style={{ fontSize: '13px', lineHeight: 1.7, color: TOKENS.muted, margin: 0, fontStyle: 'italic' }}>
              That&apos;s a taste of how Dea works — one doable move at a time. Imagine 90 days of this, built around you.
            </p>
          </div>

          {/* Gold divider */}
          <div style={{ width: 80, height: 1, background: TOKENS.gold, margin: '28px auto', opacity: 0.7 }} />

          {/* 90-day card */}
          <div
            style={{
              background: TOKENS.cardBg,
              border: `1px solid ${TOKENS.cardBorder}`,
              borderLeft: `3px solid ${TOKENS.gold}`,
              borderRadius: '12px',
              padding: '24px',
              margin: '8px 0 32px',
            }}
          >
            <p style={{ color: TOKENS.gold, fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, margin: '0 0 12px 0' }}>
              Inside Your 90-Day Journey
            </p>
            <p style={{ fontSize: '13px', lineHeight: 1.7, color: TOKENS.muted, margin: '0 0 12px 0' }}>
              Your personalized roadmap. Your daily check-ins. Your habit tracker. Your journal. Your milestone system. A coach who knows exactly where you are in your journey — every single day.
            </p>
            <p style={{ fontSize: '13px', lineHeight: 1.7, color: TOKENS.muted, margin: '0 0 8px 0' }}>
              This isn&apos;t another chatbot.
            </p>
            <p style={{ fontSize: '13px', lineHeight: 1.7, color: TOKENS.muted, margin: 0 }}>
              It&apos;s a personalized system built around your goals, your habits, your season of life, and the woman you&apos;re becoming.
            </p>
          </div>

          <div style={{ textAlign: 'center' }}>
            {(() => {
              const mirror = buildMirrorCopy(report.proof_q4, report.proof_q11);
              if (!mirror) return null;
              return (
                <p
                  style={{
                    fontFamily: TOKENS.sans,
                    fontSize: '15px',
                    lineHeight: 1.65,
                    color: TOKENS.dark,
                    margin: '0 auto 22px',
                    maxWidth: '480px',
                    textAlign: 'left',
                  }}
                  data-testid="glowup-report-mirror"
                >
                  {mirror}
                </p>
              );
            })()}
            <h3
              style={{
                fontFamily: TOKENS.serif,
                fontSize: 'clamp(18px, 4vw, 22px)',
                fontWeight: 400,
                color: TOKENS.dark,
                margin: '0 0 10px 0',
                lineHeight: 1.3,
              }}
            >
              Your next 90 days don&apos;t have to be a guessing game.
            </h3>
            <p style={{ fontFamily: TOKENS.sans, fontSize: '14px', color: TOKENS.muted, margin: '0 0 24px 0' }}>
              Start your 7-day trial with Dea for just $9.
            </p>

            <button
              type="button"
              onClick={handleStartTrial}
              disabled={checkoutLoading}
              data-testid="glowup-report-trial-cta"
              style={{
                width: '100%',
                background: TOKENS.gold,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '30px',
                padding: '18px 24px',
                fontSize: '16px',
                fontWeight: 700,
                fontFamily: TOKENS.sans,
                cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                letterSpacing: '0.01em',
              }}
            >
              Start My 7-Day Trial — $9 →
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = `/checkout?plan=lifetime&email=${encodeURIComponent(report?.email || '')}&first_name=${encodeURIComponent(report?.first_name || '')}`; }}
              data-testid="gap-lifetime-cta"
              style={{ width: '100%', marginTop: '12px', background: 'transparent', color: TOKENS.gold, border: `1.5px solid ${TOKENS.gold}`, borderRadius: '30px', padding: '16px 24px', fontSize: '15px', fontWeight: 600, fontFamily: TOKENS.sans, cursor: 'pointer' }}
            >
              Ready now? Get Lifetime Access — ${pricing.lifetime_price} one-time{pricing.is_early_access ? ` (normally $${pricing.lifetime_regular_price})` : ''}
            </button>


            {checkoutError && (
              <p style={{ color: '#B23A48', fontSize: '13px', marginTop: '12px' }}>{checkoutError}</p>
            )}

            <p style={{ fontSize: '11px', color: TOKENS.muted, marginTop: '14px', lineHeight: 1.6, maxWidth: '460px', margin: '14px auto 0' }}>
              Your $9 starts your 7 days with Dea. Nothing is charged automatically after that — ever. If you want to keep her, you can unlock lifetime access yourself for ${pricing.upgrade_price} (your $9 applies).
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}
